import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { tournamentService } from '../../services/tournamentService';
import { pandaScoreService } from '../../services/pandaScoreService';
import { tournamentNameService } from '../../services/tournamentNameService';
import { teamNameService } from '../../services/teamNameService';
import TournamentDetailModal from './TournamentDetailModal';

const TournamentDraftList = () => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, id: null });

  // 下書き一覧を取得
  const fetchDrafts = async () => {
    try {
      setLoading(true);
      const data = await tournamentService.getDraftTournaments();
      setDrafts(data);
      setError(null);
    } catch (err) {
      console.error('下書き一覧の取得に失敗:', err);
      setError('下書き一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
  }, []);

  // PandaScoreから大会情報をインポート
  const importFromPandaScore = async () => {
    setImporting(true);
    setError(null);
    
    try {
      // 今後30日間の大会を取得
      const tournaments = await pandaScoreService.getUpcomingValorantTournaments(30);
      
      let importedCount = 0;
      let skippedCount = 0;
      let newTournamentNames = new Set();
      let newTeamNames = new Set();
      
      // 既存の大会名とチーム名を取得
      const [existingTournamentNames, existingTeamNames] = await Promise.all([
        tournamentNameService.getAllTournamentNames(),
        teamNameService.getAllTeamNames()
      ]);
      
      const existingTournamentNameSet = new Set(existingTournamentNames.map(t => t.name));
      const existingTeamNameSet = new Set(existingTeamNames.map(t => t.name));
      
      for (const tournament of tournaments) {
        // 既に存在するかチェック
        const existing = await tournamentService.getTournamentByPandaScoreId(tournament.id);
        
        if (existing) {
          skippedCount++;
          continue;
        }
        
        // マッチ情報を取得
        let matches = [];
        try {
          const matchData = await pandaScoreService.getTournamentMatches(tournament.id);
          matches = matchData;
        } catch (err) {
          console.warn('マッチ情報の取得に失敗:', err);
        }
        
        // 内部フォーマットに変換
        const internalFormat = pandaScoreService.convertToInternalFormat(tournament, matches);
        
        // 新しい大会名を収集
        if (internalFormat.tournamentName && !existingTournamentNameSet.has(internalFormat.tournamentName)) {
          newTournamentNames.add(internalFormat.tournamentName);
        }
        
        // 新しいチーム名を収集
        if (internalFormat.matchups) {
          for (const matchup of internalFormat.matchups) {
            if (matchup.teams) {
              for (const team of matchup.teams) {
                if (team.name && team.name !== 'TBD' && !existingTeamNameSet.has(team.name)) {
                  newTeamNames.add(team.name);
                }
              }
            }
          }
        }
        
        // 大会を保存
        await tournamentService.createTournament(internalFormat);
        importedCount++;
      }
      
      // 新しい大会名をFirestoreに追加
      for (const tournamentName of newTournamentNames) {
        try {
          await tournamentNameService.createTournamentName({ 
            name: tournamentName,
            category: 'VALORANT',
            createdFrom: 'pandascore'
          });
          console.log('新しい大会名を追加:', tournamentName);
        } catch (err) {
          console.warn('大会名の追加に失敗:', tournamentName, err);
        }
      }
      
      // 新しいチーム名をFirestoreに追加
      for (const teamName of newTeamNames) {
        try {
          await teamNameService.createTeamName({ 
            name: teamName,
            category: 'VALORANT',
            createdFrom: 'pandascore'
          });
          console.log('新しいチーム名を追加:', teamName);
        } catch (err) {
          console.warn('チーム名の追加に失敗:', teamName, err);
        }
      }
      
      const resultMessage = `インポート完了\n新規大会: ${importedCount}件\nスキップ: ${skippedCount}件\n新規大会名: ${newTournamentNames.size}件\n新規チーム名: ${newTeamNames.size}件`;
      alert(resultMessage);
      fetchDrafts(); // リストを更新
    } catch (err) {
      console.error('PandaScoreからのインポートに失敗:', err);
      setError('大会情報のインポートに失敗しました。');
    } finally {
      setImporting(false);
    }
  };

  // 下書きを公開
  const handlePublish = async (id) => {
    try {
      await tournamentService.publishDraft(id);
      setDrafts(drafts.filter(d => d.id !== id));
      alert('大会を公開しました。');
    } catch (err) {
      console.error('公開に失敗:', err);
      alert('公開に失敗しました。');
    }
  };

  // 削除
  const handleDelete = async (id) => {
    try {
      await tournamentService.deleteTournament(id);
      setDrafts(drafts.filter(d => d.id !== id));
      alert('下書きを削除しました。');
    } catch (err) {
      console.error('削除に失敗:', err);
      alert('削除に失敗しました。');
    }
  };

  // 確認ダイアログを開く
  const openConfirmDialog = (action, id) => {
    setConfirmDialog({ open: true, action, id });
  };

  // 確認ダイアログを閉じる
  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, action: null, id: null });
  };

  // 確認後のアクション実行
  const executeAction = () => {
    const { action, id } = confirmDialog;
    if (action === 'publish') {
      handlePublish(id);
    } else if (action === 'delete') {
      handleDelete(id);
    }
    closeConfirmDialog();
  };

  const columns = [
    {
      field: 'tournamentName',
      headerName: '大会名',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
          {params.row.pandaScoreId && (
            <Chip
              label="PandaScore"
              size="small"
              sx={{
                mt: 0.5,
                height: 20,
                fontSize: '0.7rem',
                backgroundColor: 'rgba(255, 165, 0, 0.2)',
                color: '#ffa500',
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'date',
      headerName: '開催日',
      width: 120,
      renderCell: (params) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        return date.toLocaleDateString('ja-JP');
      },
    },
    {
      field: 'category',
      headerName: 'カテゴリ',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'importedAt',
      headerName: 'インポート日時',
      width: 180,
      renderCell: (params) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        return date.toLocaleString('ja-JP');
      },
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="詳細">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedTournament(params.row);
                setDetailModalOpen(true);
              }}
              sx={{
                color: '#00aaff',
                '&:hover': {
                  color: '#00ffff',
                  background: 'rgba(0, 255, 255, 0.1)',
                }
              }}
            >
              <InfoIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="編集">
            <IconButton
              size="small"
              onClick={() => navigate(`/admin/tournaments/edit/${params.row.id}`)}
              sx={{
                color: '#ff00ff',
                '&:hover': {
                  color: '#ff66ff',
                  background: 'rgba(255, 0, 255, 0.1)',
                }
              }}
            >
              <EditIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="公開">
            <IconButton
              size="small"
              onClick={() => openConfirmDialog('publish', params.row.id)}
              sx={{
                color: '#00ff00',
                '&:hover': {
                  color: '#66ff66',
                  background: 'rgba(0, 255, 0, 0.1)',
                }
              }}
            >
              <CheckCircleIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="削除">
            <IconButton
              size="small"
              onClick={() => openConfirmDialog('delete', params.row.id)}
              sx={{
                color: '#ff0066',
                '&:hover': {
                  color: '#ff3388',
                  background: 'rgba(255, 0, 102, 0.1)',
                }
              }}
            >
              <DeleteIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
          下書き大会管理
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          PandaScore APIから取得した大会情報が下書きとして表示されます。
          内容を確認・編集してから公開してください。
        </Alert>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={importing ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
          onClick={importFromPandaScore}
          disabled={importing}
          sx={{
            background: 'linear-gradient(45deg, #ff6b00 30%, #ff8f00 90%)',
            '&:hover': {
              background: 'linear-gradient(45deg, #ff8f00 30%, #ffab00 90%)',
            }
          }}
        >
          {importing ? 'インポート中...' : 'PandaScoreから取得'}
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchDrafts}
          disabled={loading}
        >
          更新
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{
        borderRadius: 0,
        boxShadow: '0 0 30px rgba(0, 255, 255, 0.2)',
        border: '2px solid rgba(0, 255, 255, 0.3)',
        backgroundColor: 'rgba(37, 37, 48, 0.9)',
        backdropFilter: 'blur(10px)',
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={drafts}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderColor: 'rgba(0, 255, 255, 0.1)',
                padding: '8px 12px',
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'rgba(0, 255, 255, 0.05)',
                borderColor: 'rgba(0, 255, 255, 0.2)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  bgcolor: 'rgba(0, 255, 255, 0.05)',
                },
              },
            }}
          />
        )}
      </Paper>

      {/* 詳細モーダル */}
      <TournamentDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedTournament(null);
        }}
        tournament={selectedTournament}
      />

      {/* 確認ダイアログ */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: '2px solid rgba(0, 255, 255, 0.3)',
            bgcolor: 'rgba(37, 37, 48, 0.95)',
          }
        }}
      >
        <DialogTitle>
          {confirmDialog.action === 'publish' ? '大会を公開' : '下書きを削除'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'publish'
              ? 'この大会を公開してもよろしいですか？'
              : 'この下書きを削除してもよろしいですか？'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog}>キャンセル</Button>
          <Button
            onClick={executeAction}
            variant="contained"
            color={confirmDialog.action === 'publish' ? 'success' : 'error'}
          >
            {confirmDialog.action === 'publish' ? '公開' : '削除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TournamentDraftList;
